import React, { useRef, useState } from "react";
import { X } from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import DatePicker from "../../../../components/DatePicker";
import { parseEgyptianNationalId, normalizeNationalId } from "../../../../utils/egyptianNationalId";
import { API_URL } from "../../config";

// ===== RESPONSIVE ADD EMPLOYEE MODAL COMPONENT =====
const AddEmployeeModal = ({
  styles,
  text,
  language,
  theme,
  screenSize,
  onAdd,
  onClose,
}) => {
  // Initialize form data with empty values
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    department: "",
    position: "",
    laborMode: "",
    lastContractTerm: "",
    company: "",
    clientCode: "",
    status: "Active",
    salary: "",
    grossSalary: "",
    salaryInputSource: "",
    nationalId: "",
    address: "",
    city: "",
    country: "Egypt",
    nationality: "",
    gender: "",
    maritalStatus: "",
    dateOfBirth: "",
  });

  // Form validation errors state
  const [formErrors, setFormErrors] = useState({});

  // Handle input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Clear error when user starts typing
    if (formErrors[e.target.name]) {
      setFormErrors({
        ...formErrors,
        [e.target.name]: "",
      });
    }
  };

  // Typing a full Egyptian national ID fills date of birth and gender from it
  // (overwriting, the ID is the source of truth), plus nationality only when
  // it's still empty. One functional update so the fields don't clobber each other.
  const nationalIdInfo = parseEgyptianNationalId(formData.nationalId);
  const nationalIdInvalid =
    normalizeNationalId(formData.nationalId).length >= 14 && !nationalIdInfo;
  const handleNationalIdChange = (e) => {
    const value = e.target.value;
    const info = parseEgyptianNationalId(value);
    setFormData((prev) => ({
      ...prev,
      nationalId: value,
      ...(info && {
        dateOfBirth: info.dateOfBirth,
        gender: info.gender,
        nationality: prev.nationality || (language === "ar" ? "مصري" : "Egyptian"),
      }),
    }));
  };

  // Gross/Net salary are mutually derived: whichever one the user types into
  // becomes `salaryInputSource` (sent to the backend as the authoritative
  // figure on save); the other field locks and shows a live preview fetched
  // from the server — same formulas the save itself will use, never
  // duplicated in the frontend.
  const salaryPreviewTimer = useRef(null);
  const handleSalaryFieldChange = (name, value) => {
    const source = name === "grossSalary" ? "gross" : "net";
    setFormData((prev) => ({ ...prev, [name]: value, salaryInputSource: source }));

    if (salaryPreviewTimer.current) clearTimeout(salaryPreviewTimer.current);
    if (value === "" || value === null || isNaN(Number(value))) return;

    salaryPreviewTimer.current = setTimeout(async () => {
      try {
        const response = await apiFetch(`${API_URL}/employees/salary-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            grossSalary: source === "gross" ? value : null,
            netSalary: source === "net" ? value : null,
            salaryInputSource: source,
            insuranceSalary: formData.insuranceSalary || null,
          }),
        });
        const data = await response.json();
        if (data?.success) {
          setFormData((prev) => ({
            ...prev,
            grossSalary: source === "gross" ? prev.grossSalary : data.grossSalary ?? prev.grossSalary,
            salary: source === "net" ? prev.salary : data.netSalary ?? prev.salary,
          }));
        }
      } catch (err) {
        console.error("Salary preview failed:", err);
      }
    }, 400);
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    // Check required fields
    if (!formData.name.trim()) {
      errors.name = text[language].nameRequired;
    }

    if (!formData.email.trim()) {
      errors.email = text[language].emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = text[language].emailInvalid;
    }

    if (!formData.department.trim()) {
      errors.department = text[language].departmentRequired;
    }

    if (!formData.position.trim()) {
      errors.position = text[language].positionRequired;
    }

    // Client Code is mandatory — the backend rejects an add without it
    // (except for the "Infinity" client, which uses the Infinity Code).
    const activeClient = (localStorage.getItem("clientName") || "").trim().toLowerCase();
    if (activeClient !== "infinity" && !String(formData.clientCode ?? "").trim()) {
      errors.clientCode = text[language].clientCodeRequired || "Client Code is required";
    }

    // Update errors state
    setFormErrors(errors);
    // Return true if no errors
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only proceed if form is valid
    if (!validateForm()) {
      return;
    }

    // Call add employee function
    await onAdd(formData);
  };

  // Return modal JSX
  return (
    <div
      style={styles.modal}
      onClick={onClose}
    >
      <div
        style={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{text[language].addNewEmployee}</h2>
          <button
            style={styles.closeBtn}
            onClick={onClose}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].basicInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].fullName} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.name
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterFullName}
              />
              {formErrors.name && (
                <span style={styles.errorText}>{formErrors.name}</span>
              )}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].emailAddress} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.email
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterEmail}
              />
              {formErrors.email && (
                <span style={styles.errorText}>{formErrors.email}</span>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].phoneNumber}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterPhone}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].nationalId}
              </label>
              <input
                type="text"
                inputMode="numeric"
                name="nationalId"
                value={formData.nationalId}
                onChange={handleNationalIdChange}
                style={{
                  ...styles.formInput,
                  ...(nationalIdInvalid && { borderColor: "#ef4444" }),
                }}
                placeholder={text[language].enterNationalId}
              />
              {(nationalIdInfo || nationalIdInvalid) && (
                <p style={{ fontSize: 12, margin: "6px 0 0", color: nationalIdInvalid ? "#ef4444" : "#22c55e" }}>
                  {nationalIdInvalid
                    ? (language === "ar" ? "الرقم القومي غير صحيح — لم يتم ملء البيانات تلقائيًا." : "Invalid national ID — details were not auto-filled.")
                    : (language === "ar"
                        ? "✓ تم ملء تاريخ الميلاد والنوع تلقائيًا من الرقم القومي."
                        : "✓ Date of birth and gender were filled from the national ID.")}
                </p>
              )}
            </div>
          </div>

          {/* Work Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].workInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].department} *
              </label>
              <ReusableDropdown
                name="department"
                items={[
                  { id: "Information Technology", label: text[language].infotech },
                  { id: "Human Resources", label: text[language].humanResources },
                  { id: "Finance", label: text[language].finance },
                  { id: "Marketing", label: text[language].marketing },
                  { id: "Operations", label: text[language].operations },
                  { id: "Sales", label: text[language].sales },
                  { id: "Customer Service", label: text[language].customerService },
                ]}
                value={formData.department}
                onChange={(value) => handleInputChange({ target: { name: "department", value } })}
                placeholder={text[language].selectDepartment}
                inputStyle={formErrors.department ? { borderColor: "#ef4444" } : undefined}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
              {formErrors.department && (
                <span style={styles.errorText}>{formErrors.department}</span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].position} *
              </label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.position
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterPosition}
              />
              {formErrors.position && (
                <span style={styles.errorText}>{formErrors.position}</span>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].laborMode}
              </label>
              <ReusableDropdown
                name="laborMode"
                items={[
                  { id: "Insource", label: text[language].insource },
                  { id: "Outsource", label: text[language].outsource },
                ]}
                value={formData.laborMode}
                onChange={(value) => handleInputChange({ target: { name: "laborMode", value } })}
                placeholder={text[language].selectLaborMode}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].lastContractTerm}
              </label>
              <ReusableDropdown
                name="lastContractTerm"
                items={[
                  { id: "Full-time", label: text[language].fullTime },
                  { id: "Part-time", label: text[language].partTime },
                  { id: "Contract", label: text[language].contract },
                  { id: "Temporary", label: text[language].temporary },
                  { id: "Freelance", label: text[language].freelance },
                ]}
                value={formData.lastContractTerm}
                onChange={(value) => handleInputChange({ target: { name: "lastContractTerm", value } })}
                placeholder={text[language].selectLastContractTerm}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>
          </div>

          {/* Client Code — required, the employee is not added without it. */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              {text[language].clientCode} *
            </label>
            <input
              type="text"
              name="clientCode"
              value={formData.clientCode}
              onChange={handleInputChange}
              style={{
                ...styles.formInput,
                ...(formErrors.clientCode && { borderColor: "#ef4444" }),
              }}
              placeholder={text[language].enterClientCode}
            />
            {formErrors.clientCode && (
              <span style={styles.errorText}>{formErrors.clientCode}</span>
            )}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].statusLabel}
              </label>
              <ReusableDropdown
                name="status"
                items={[
                  { id: "Active", label: text[language].statusActive },
                  { id: "OnLeave", label: text[language].statusOnLeave },
                  { id: "Inactive", label: text[language].statusInactive },
                ]}
                value={formData.status}
                onChange={(value) => handleInputChange({ target: { name: "status", value } })}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>

          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {language === "ar" ? "الراتب الإجمالي" : "Gross Salary"}
              </label>
              <input
                type="number"
                name="grossSalary"
                value={formData.grossSalary}
                onChange={(e) => handleSalaryFieldChange("grossSalary", e.target.value)}
                style={styles.formInput}
                placeholder={language === "ar" ? "أدخل الراتب الإجمالي" : "Enter gross salary"}
                min="0"
                step="any"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].salaryLabel}
              </label>
              <input
                type="number"
                name="salary"
                value={formData.salary}
                onChange={(e) => handleSalaryFieldChange("salary", e.target.value)}
                style={styles.formInput}
                placeholder={language === "ar" ? "أدخل الصافي" : "Enter net salary"}
                min="0"
                step="any"
              />
            </div>
          </div>

          {/* Personal Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].personalInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].dateOfBirth}
              </label>
              <DatePicker
                theme={theme}
                isRTL={language === "ar"}
                value={formData.dateOfBirth}
                onChange={(v) => handleInputChange({ target: { name: "dateOfBirth", value: v } })}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].gender}</label>
              <ReusableDropdown
                name="gender"
                items={[
                  { id: "Select Gender", label: text[language].selectGender },
                  { id: "Male", label: text[language].male },
                  { id: "Female", label: text[language].female },
                ]}
                value={formData.gender}
                onChange={(value) => handleInputChange({ target: { name: "gender", value } })}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].maritalStatus}
              </label>
              <ReusableDropdown
                name="maritalStatus"
                items={[
                  { id: "Single", label: text[language].single },
                  { id: "Married", label: text[language].married },
                  { id: "Divorced", label: text[language].divorced },
                  { id: "Widowed", label: text[language].widowed },
                ]}
                value={formData.maritalStatus}
                onChange={(value) => handleInputChange({ target: { name: "maritalStatus", value } })}
                placeholder={text[language].selectMaritalStatus}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].nationality}
              </label>
              <input
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterNationality}
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].city}</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterCity}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].country}</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterCountry}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{text[language].address}</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={styles.formInput}
              placeholder={text[language].enterAddress}
            />
          </div>

          {/* Modal Actions */}
          <div style={styles.modalActions}>
            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={onClose}
            >
              {text[language].cancel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployeeModal;
